import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '@wundo/ui';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { deleteJourney, stopJourney } from '@/lib/journeys.ts';

/** What staff asked to do to which journey, waiting for their confirmation. */
export interface JourneyAction {
  kind: 'stop' | 'delete';
  journey: { id: string; employee_name: string };
}

export interface JourneyActionDialogsProps {
  organisationId: string;
  /** Null while nothing is being confirmed. */
  action: JourneyAction | null;
  onClose: () => void;
  /** After a delete went through: the journey's own page has nothing left to show. */
  onDeleted?: () => void;
}

/** The confirmation before a journey is stopped or deleted, for the list and the detail page alike. */
export function JourneyActionDialogs({
  organisationId,
  action,
  onClose,
  onDeleted,
}: JourneyActionDialogsProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const run = useMutation({
    mutationFn: ({ kind, journey }: JourneyAction) =>
      kind === 'stop'
        ? stopJourney(organisationId, journey.id)
        : deleteJourney(organisationId, journey.id),
    onSuccess: async (_result, { kind, journey }) => {
      close();
      if (kind === 'delete') {
        queryClient.removeQueries({ queryKey: ['journey', organisationId, journey.id] });
        onDeleted?.();
      }
      await queryClient.invalidateQueries({ queryKey: ['journeys', organisationId] });
      if (kind === 'stop') {
        await queryClient.invalidateQueries({
          queryKey: ['journey', organisationId, journey.id],
        });
      }
    },
    onError: (failure, { kind }) =>
      setError(
        apiErrorMessage(
          failure,
          kind === 'stop' ? 'Failed to stop the journey' : 'Failed to delete the journey',
        ),
      ),
  });

  const name = action?.journey.employee_name ?? '';

  function close() {
    setError('');
    onClose();
  }

  return (
    <>
      <ConfirmDialog
        open={action?.kind === 'stop'}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title='Stop journey?'
        description={`Stop the journey of ${name}? It stays visible to the manager, marked as stopped. Reminders stop; meetings and reports stay.`}
        confirmLabel='Stop journey'
        cancelLabel='Cancel'
        error={action?.kind === 'stop' ? error : undefined}
        onConfirm={async () => {
          if (!action) return;
          setError('');
          await run.mutateAsync(action).catch(() => undefined);
        }}
      />
      <ConfirmDialog
        open={action?.kind === 'delete'}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title='Delete journey?'
        description={`Delete the journey of ${name}? It disappears for the manager too. Its meetings and reports stay as they are.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={action?.kind === 'delete' ? error : undefined}
        onConfirm={async () => {
          if (!action) return;
          setError('');
          await run.mutateAsync(action).catch(() => undefined);
        }}
      />
    </>
  );
}
